import React, { useState, useEffect, useCallback, useRef } from 'react';
import { clearPatientLink, peekPatientLink, peekPatientPrefill } from '../utils/patientLink';
import { formatPatientName } from '../utils/formatName';
import './EligibilityPage.css';
import EligibilityResults from '../components/EligibilityResults';
import PayerIdInput from '../components/PayerIdInput';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';

const API_URL = process.env.REACT_APP_API_URL || '';

// Service type codes the user can break out into their own results box.
const SERVICE_TYPE_OPTIONS = [
  { code: '30', label: 'Health Benefit Plan Coverage (general)' },
  { code: '42', label: 'Home Health Care' },
  { code: '1', label: 'Medical Care' },
  { code: '33', label: 'Chiropractic' },
  { code: '35', label: 'Dental Care' },
  { code: '47', label: 'Hospital' },
  { code: '48', label: 'Hospital - Inpatient' },
  { code: '50', label: 'Hospital - Outpatient' },
  { code: '51', label: 'Hospital - Emergency Accident' },
  { code: '52', label: 'Hospital - Emergency Medical' },
  { code: '86', label: 'Emergency Services' },
  { code: '88', label: 'Pharmacy' },
  { code: '98', label: 'Professional (Physician) Visit - Office' },
  { code: 'AL', label: 'Vision (Optometry)' },
  { code: 'BZ', label: 'Physician Visit - Office: Well' },
  { code: 'MH', label: 'Mental Health' },
  { code: 'UC', label: 'Urgent Care' },
  { code: 'A3', label: 'Physician Visit - Home' },
  { code: 'A8', label: 'Psychiatric' },
];

// A name part in proper case for the form; empty stays empty (formatPatientName
// shows a dash for a missing name, which must not land in an input).
const properName = (value) => (value ? formatPatientName(value) : '');

// Lowercase, no spaces: "United Healthcare" matches "UnitedHealthcare".
const normName = (n) => (n || '').toLowerCase().replace(/\s+/g, '');

const providerDisplayName = (prov) =>
  prov.name || `${prov.first_name || ''} ${prov.last_name || ''}`.trim() || prov.npi || '';

// The practice provider an appointment's provider_name names ("Dana Demoprovider", or
// "Demoprovider, Dana" as some EMRs export it), or null.
const findProviderByName = (providers, name) => {
  if (!name) return null;
  const wanted = normName(name);
  const flipped = name.includes(',') ? normName(name.split(',').reverse().join(' ')) : null;
  return (providers || []).find((prov) => {
    const have = normName(providerDisplayName(prov));
    return have && (have === wanted || have === flipped);
  }) || null;
};

// One page for every role: the page the client sees (David's reference, 2026-10-23
// 19:55Z: main's page, the full check form with date of service, provider, service
// type, subscriber, payer search, Check Eligibility, and the results/history below).
// A client may run a check (Stedi spend approved, Jordan 2026-10-23). The staff-only
// upcoming/Stedi table (#460) and Send to practice are off this page for everyone
// (David, 2026-10-25): eligibility is read here, not sent out as a roster.
function EligibilityPage({ selectedPractice, onPracticeUpdated }) {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [formFields, setFormFields] = useState({
    firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '',
  });

  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [dateOfService, setDateOfService] = useState(new Date().toISOString().slice(0, 10));
  // Service type code to request (one per check) — "30" by default.
  const [serviceType, setServiceType] = useState('30');

  const [results, setResults] = useState(null);
  const [eligHistory, setEligHistory] = useState([]);
  const [activeView, setActiveView] = useState('history');
  const [historyResultData, setHistoryResultData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Payer search
  const [payerSearchTerm, setPayerSearchTerm] = useState('');
  const [payerSearchResults, setPayerSearchResults] = useState([]);
  const [payerSearching, setPayerSearching] = useState(false);
  const [showPayerSearch, setShowPayerSearch] = useState(false);

  // The practice payer picked in the form (its practice_payers row id). Separate from
  // formFields.payerId so a payer with no Stedi id yet can still be the selected one,
  // with its Payer ID field beside the picker waiting for the id.
  const [selectedPayerRowId, setSelectedPayerRowId] = useState(null);
  // Payer ids saved from this page, until the practice is reloaded with them.
  const [payerOverrides, setPayerOverrides] = useState({});
  const [payerIdError, setPayerIdError] = useState(null);
  const [focusPayerIdPending, setFocusPayerIdPending] = useState(false);
  const payerIdInputRef = useRef(null);
  // Provider named by the visit that opened this page; survives App.js replacing the
  // practice object after the navigation (which re-runs the default-provider effect).
  const prefillProviderName = useRef(null);

  const practicePayers = (selectedPractice?.payers || []).map((p) =>
    payerOverrides[p.id] !== undefined ? { ...p, payer_id: payerOverrides[p.id] } : p);
  useEffect(() => { setPayerOverrides({}); }, [selectedPractice?.payers]);
  const formPanelRef = useRef(null);

  // The practice payer a (payer id, payer name) pair names: id+name first (UHC and
  // Optum share an id), then id alone, then — for a payer with no id yet — name alone.
  const findPracticePayer = (payerId, payerName) => {
    const id = (payerId || '').trim();
    const name = normName(payerName);
    if (id) {
      return practicePayers.find((p) => p.payer_id === id && normName(p.payer_name) === name)
        || practicePayers.find((p) => p.payer_id === id)
        || null;
    }
    if (!name) return null;
    // Two practice payers can share a name (the Tebra sync makes one per spelling and
    // the mapping script fills the id on the row it found): the one that carries a
    // Stedi id is the one a check can use.
    const byName = practicePayers.filter((p) => normName(p.payer_name) === name);
    return byName.find((p) => (p.payer_id || '').trim()) || byName[0] || null;
  };

  // Normalize DOB to YYYY-MM-DD for date inputs (handles M/D/YYYY, MM/DD/YYYY, YYYY-MM-DD)
  const normalizeDob = (dob) => {
    if (!dob) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob;
    const parts = dob.split('/');
    if (parts.length === 3) {
      const [m, d, y] = parts;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dob;
  };

  useEffect(() => {
    const providers = selectedPractice?.providers || [];
    const wanted = findProviderByName(providers, prefillProviderName.current);
    if (wanted) {
      setSelectedProviderId(wanted.id);
    } else if (providers.length > 0) {
      setSelectedProviderId(providers[0].id);
    } else {
      setSelectedProviderId('');
    }
  }, [selectedPractice]);

  // Clear the patient on a real practice change only. App.js replaces the practice
  // object with a fuller copy after a navigation, and StrictMode runs this twice on
  // mount; resetting on either dropped a patient opened from the Appointments page.
  const appliedPracticeId = useRef(undefined);
  useEffect(() => {
    if (appliedPracticeId.current === selectedPractice?.id) return;
    appliedPracticeId.current = selectedPractice?.id;
    setSelectedPatient(null);
    setResults(null);
    setEligHistory([]);
    setActiveView('history');
    setFormFields({ firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '' });
    setSelectedPayerRowId(null);
    setPayerIdError(null);
    prefillProviderName.current = null;
  }, [selectedPractice?.id]);

  const loadPatients = useCallback(async () => {
    if (!selectedPractice) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
      } else {
        // The link effect consumes a pending prefill only once the roster is here. A
        // prefill carries a member id and DOB, so one that cannot land now must not
        // wait for the next visit to this page.
        clearPatientLink();
      }
    } catch (err) {
      clearPatientLink();
    }
  }, [selectedPractice]);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  const loadHistory = useCallback(async (patientId) => {
    if (!selectedPractice || !patientId) return;
    try {
      const response = await fetch(`${API_URL}/api/eligibility/history/${selectedPractice.id}/${patientId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEligHistory(data.checks || []);
      }
    } catch (err) { /* supplementary */ }
  }, [selectedPractice]);

  const filteredPatients = patients.filter(p => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return (p.display_name || '').toLowerCase().includes(term) || (p.subscriber_id || '').toLowerCase().includes(term);
  });

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
    // A roster row often names the payer and carries no payer code (Tebra, Athena); the
    // Stedi id then comes from the practice payer the name resolves to.
    const payerRow = findPracticePayer(patient.payer_id, patient.payer_name);
    setFormFields({
      firstName: properName(patient.subscriber_first_name),
      lastName: properName(patient.subscriber_last_name),
      dob: normalizeDob(patient.subscriber_dob),
      memberId: patient.subscriber_id || '',
      payerId: patient.payer_id || (payerRow && payerRow.payer_id) || '',
      payerName: patient.payer_name || (payerRow && payerRow.payer_name) || '',
    });
    setSelectedPayerRowId(payerRow ? payerRow.id : null);
    setPayerIdError(null);
    setResults(null);
    setError(null);
    setActiveView('history');
    setHistoryResultData(null);
    loadHistory(patient.id);
  };

  // "Check Eligibility" on the Appointments page lands here with that patient
  // selected, once the practice's patient list has loaded. When the row brought the
  // visit's coverage along, the form is filled from it (same path as the upcoming
  // table's Check); a payer with no Stedi id yet gets the Payer ID field focused.
  useEffect(() => {
    const linkedId = peekPatientLink('eligibility', selectedPractice?.id);
    if (!linkedId || patients.length === 0) return;
    const prefill = peekPatientPrefill('eligibility', selectedPractice?.id);
    clearPatientLink();
    if (prefill) {
      prefillFromRow({
        patient_id: linkedId,
        dob: prefill.dob,
        coverage: prefill.coverage,
        provider_name: prefill.provider_name,
        appointment_time: prefill.appointment_date,
      }, { focusMissingPayerId: true });
      return;
    }
    const patient = patients.find((p) => p.id === linkedId);
    if (patient) handleSelectPatient(patient);
  }, [patients, selectedPractice]);

  // Focus the Payer ID field once it is on the page (it renders with the selected payer).
  useEffect(() => {
    if (!focusPayerIdPending) return;
    const el = payerIdInputRef.current;
    if (el) {
      el.focus();
      setFocusPayerIdPending(false);
    }
  }, [focusPayerIdPending, selectedPayerRowId]);

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setFormFields({ firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '' });
    setSelectedPayerRowId(null);
    setPayerIdError(null);
    prefillProviderName.current = null;
    setResults(null);
    setError(null);
    setEligHistory([]);
    setActiveView('history');
    setHistoryResultData(null);
  };

  const handleFieldChange = (field, value) => {
    setFormFields(prev => ({ ...prev, [field]: value }));
  };

  // Prefill the manual form from an appointment row — the Appointments page's "Check
  // Eligibility" lands here: the patient, the visit's coverage (payer, member id), DOB,
  // provider and the visit date.
  // The check itself is NOT run — the "Check Eligibility" button stays the one that
  // spends a Stedi call.
  const prefillFromRow = (row, { focusMissingPayerId = false } = {}) => {
    const hasPatientId = row.patient_id !== null && row.patient_id !== undefined;
    let patient = hasPatientId ? patients.find(p => String(p.id) === String(row.patient_id)) : null;
    if (!patient && hasPatientId) {
      // Not in the loaded list (new since load): build the minimal patient the form
      // needs from the row itself, keyed by patient_id so history still loads.
      const name = row.patient_name || '';
      const [last, first] = name.includes(',')
        ? name.split(',').map(x => x.trim())
        : [name.split(' ').slice(-1)[0] || '', name.split(' ').slice(0, -1).join(' ')];
      patient = {
        id: row.patient_id,
        display_name: name,
        subscriber_first_name: first,
        subscriber_last_name: last,
        subscriber_dob: row.dob || '',
        subscriber_id: row.coverage?.member_id || '',
        payer_id: row.coverage?.payer_id || '',
        payer_name: row.coverage?.payer_name || '',
      };
    }
    if (patient) {
      handleSelectPatient(patient);
    } else {
      handleClearPatient();
    }
    // The visit's coverage is what the check is for, so it wins over the roster row.
    const coverage = row.coverage;
    if (coverage) {
      const dob = row.dob ? normalizeDob(row.dob) : '';
      const namesPayer = Boolean(coverage.payer_id || coverage.payer_name);
      const payerRow = namesPayer ? findPracticePayer(coverage.payer_id, coverage.payer_name) : null;
      setFormFields(prev => ({
        ...prev,
        memberId: coverage.member_id || prev.memberId,
        dob: dob || prev.dob,
        ...(namesPayer
          ? {
            // The visit's payer code when it has one, else the practice payer's Stedi id.
            payerId: coverage.payer_id || (payerRow && payerRow.payer_id) || '',
            payerName: coverage.payer_name || (payerRow && payerRow.payer_name) || '',
          }
          : {}),
      }));
      if (namesPayer) {
        setSelectedPayerRowId(payerRow ? payerRow.id : null);
        if (focusMissingPayerId && payerRow && !(payerRow.payer_id || '').trim()) {
          setFocusPayerIdPending(true);
        }
      }
    }
    if (row.provider_name) {
      prefillProviderName.current = row.provider_name;
      const prov = findProviderByName(selectedPractice?.providers, row.provider_name);
      if (prov) setSelectedProviderId(prov.id);
    }
    if (row.appointment_time) setDateOfService(String(row.appointment_time).slice(0, 10));
    const el = formPanelRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // A payer id saved from the inline field: the picker keeps that payer, the form
  // carries the new id, and the practice is refreshed so every page sees it.
  const handlePayerIdSaved = (updated) => {
    setPayerOverrides(prev => ({ ...prev, [updated.id]: updated.payer_id || '' }));
    setSelectedPayerRowId(updated.id);
    setFormFields(prev => ({ ...prev, payerId: updated.payer_id || '', payerName: updated.payer_name || prev.payerName }));
    setPayerIdError(null);
    if (onPracticeUpdated) onPracticeUpdated();
  };


  const handlePayerChange = (e) => {
    const val = e.target.value;
    setPayerIdError(null);
    if (!val) {
      setSelectedPayerRowId(null);
      setFormFields(prev => ({ ...prev, payerId: '', payerName: '' }));
      return;
    }
    const payer = practicePayers.find(p => String(p.id) === val);
    if (payer) {
      setSelectedPayerRowId(payer.id);
      setFormFields(prev => ({ ...prev, payerId: payer.payer_id || '', payerName: payer.payer_name }));
    }
  };

  // The practice payer the picker shows: the one picked here, else the one the form's
  // payer id + name match (a patient's payer, filled in by handleSelectPatient).
  const selectedPayerRow = (selectedPayerRowId != null
    && practicePayers.find(p => String(p.id) === String(selectedPayerRowId)))
    || findPracticePayer(formFields.payerId, formFields.payerName)
    || null;
  // The payer id a check runs with: the form's, else the selected practice payer's
  // stored Stedi id. The button is disabled only when neither has one.
  const effectivePayerId = formFields.payerId || (selectedPayerRow && selectedPayerRow.payer_id) || '';

  const handlePayerSearch = useCallback(async (term) => {
    setPayerSearchTerm(term);
    if (term.length < 2) { setPayerSearchResults([]); return; }
    setPayerSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/stedi/payers/search?query=${encodeURIComponent(term)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPayerSearchResults(data.payers || []);
      }
    } catch { /* search is supplementary */ }
    finally { setPayerSearching(false); }
  }, []);

  const handleSelectSearchPayer = (p) => {
    setSelectedPayerRowId(null);
    setFormFields(prev => ({ ...prev, payerId: p.payer_id, payerName: p.name }));
    setPayerSearchTerm('');
    setPayerSearchResults([]);
    setShowPayerSearch(false);
  };

  const handleCheck = async () => {
    if (!effectivePayerId || !formFields.memberId) return;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const response = await fetch(`${API_URL}/api/eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          practiceId: selectedPractice.id,
          patientId: selectedPatient?.id || null,
          providerId: selectedProviderId || null,
          memberId: formFields.memberId,
          firstName: formFields.firstName,
          lastName: formFields.lastName,
          dateOfBirth: formFields.dob,
          payerId: effectivePayerId,
          payerName: formFields.payerName,
          dateOfService: dateOfService,
          // Always request 30 (plan-level) alongside the chosen type — plan-level
          // deductible/out-of-pocket and the network indicators often appear only
          // under 30. Same single 270, same cost.
          serviceTypeCodes: [...new Set([serviceType, '30'])],
        }),
      });
      const data = await response.json();
      if (data.success) {
        setResults(data.data);
        setActiveView('results');
        if (selectedPatient) loadHistory(selectedPatient.id);
        loadPatients();
      } else {
        setError(data.error || 'Failed to check eligibility');
        if (data.data) { setResults(data.data); setActiveView('results'); }
      }
    } catch (err) {
      setError('Network error. Please ensure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const formatCheckedAt = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDos = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const canSubmit = effectivePayerId && formFields.memberId && formFields.firstName && formFields.lastName && formFields.dob && !loading;

  if (!selectedPractice) {
    return (
      <div className="eligibility-page">
        <h1 className="page-title">Eligibility</h1>
        <div className="empty-state"><p>Select a practice to check eligibility.</p></div>
      </div>
    );
  }

  return (
    <div className="eligibility-page">
      <h1 className="page-title">Eligibility</h1>

      <div className="eligibility-content">
        {/* LEFT PANEL: the patient search and the check form, for every role */}
        <div className="form-panel" ref={formPanelRef}>
          <div className="elig-card">
            {/* DOS + Provider */}
            <div className="form-row">
              <div className="form-field">
                <label>Date of Service</label>
                <input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={dateOfService} onChange={(e) => setDateOfService(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Provider</label>
                <select value={selectedProviderId} onChange={(e) => { prefillProviderName.current = null; setSelectedProviderId(e.target.value); }}>
                  {(selectedPractice.providers || []).map(prov => (
                    <option key={prov.id} value={prov.id}>
                      {providerDisplayName(prov)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Service Type to request (270) — one per check */}
            <div className="form-row">
              <div className="form-field full">
                <label>Service Type</label>
                <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
                  {SERVICE_TYPE_OPTIONS.map(o => (
                    <option key={o.code} value={o.code}>{o.code} — {o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-divider" />

            {/* Patient */}
            <div className="form-row">
              <div className="form-field full">
                <label>Patient</label>
                <div className="patient-search-wrapper">
                  {selectedPatient ? (
                    <div className="selected-patient-tag">
                      <span>{formatPatientName(selectedPatient.display_name)}</span>
                      <button className="sp-clear" onClick={handleClearPatient}>&times;</button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Search by name or member ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && filteredPatients.length > 0 && (
                        <div className="patient-dropdown">
                          {filteredPatients.slice(0, 10).map((p) => (
                            <div key={p.id} className="patient-option" onClick={() => handleSelectPatient(p)}>
                              <div className="patient-option-name">{formatPatientName(p.display_name)}</div>
                              <div className="patient-option-detail">
                                {p.payer_name || 'No payer'} &middot; {p.subscriber_id || 'No member ID'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchTerm && filteredPatients.length === 0 && (
                        <div className="patient-dropdown">
                          <div className="patient-option-empty">No patients found</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="form-divider" />

            {/* Subscriber Info */}
            <div className="form-section-label">Subscriber Info</div>
            <div className="form-row">
              <div className="form-field">
                <label>First Name</label>
                <input type="text" value={formFields.firstName} onChange={(e) => handleFieldChange('firstName', e.target.value)} placeholder="John" />
              </div>
              <div className="form-field">
                <label>Last Name</label>
                <input type="text" value={formFields.lastName} onChange={(e) => handleFieldChange('lastName', e.target.value)} placeholder="Doe" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Date of Birth</label>
                <input type="date" min="1900-01-01" max={todayStr} value={formFields.dob} onChange={(e) => handleFieldChange('dob', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Member ID</label>
                <input type="text" value={formFields.memberId} onChange={(e) => handleFieldChange('memberId', e.target.value)} placeholder="ABC123456" />
              </div>
            </div>

            {/* Payer */}
            <div className="form-row">
              <div className="form-field full">
                <label>Payer</label>
                {/* A payer from "Search all payers" (not one of the practice's) shows as a chip;
                    a practice payer stays in the picker, with its Stedi id editable beside it. */}
                {formFields.payerId && !selectedPayerRow ? (
                  <div className="payer-manual-display">
                    <span>{formFields.payerName || formFields.payerId}</span>
                    <span className="payer-manual-id">{formFields.payerId}</span>
                    <button className="payer-manual-clear" onClick={() => { setSelectedPayerRowId(null); setFormFields(prev => ({ ...prev, payerId: '', payerName: '' })); setShowPayerSearch(false); }}>&times;</button>
                  </div>
                ) : showPayerSearch ? (
                  <div className="patient-search-wrapper">
                    <input
                      type="text"
                      placeholder="Search by payer name or ID..."
                      value={payerSearchTerm}
                      onChange={(e) => handlePayerSearch(e.target.value)}
                      autoFocus
                    />
                    {payerSearchTerm.length >= 2 && (
                      <div className="patient-dropdown">
                        {payerSearching && <div className="patient-option-empty">Searching...</div>}
                        {!payerSearching && payerSearchResults.length === 0 && payerSearchTerm.length >= 2 && (
                          <div className="patient-option-empty">No payers found</div>
                        )}
                        {payerSearchResults.map((p, i) => (
                          <div key={i} className="patient-option" onClick={() => handleSelectSearchPayer(p)}>
                            <div className="patient-option-name">{p.name}</div>
                            <div className="patient-option-detail">{p.payer_id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="payer-search-back" onClick={() => setShowPayerSearch(false)}>Back to list</button>
                  </div>
                ) : (
                  <>
                    <div className="payer-select-row">
                      <select className="payer-select" value={selectedPayerRow ? String(selectedPayerRow.id) : ''} onChange={handlePayerChange}>
                        <option value="">Select a payer...</option>
                        {practicePayers.map(p => (
                          <option key={p.id} value={String(p.id)}>{p.payer_name}</option>
                        ))}
                      </select>
                      {selectedPayerRow && (
                        <PayerIdInput
                          practiceId={selectedPractice.id}
                          payer={selectedPayerRow}
                          inputRef={payerIdInputRef}
                          onSaved={handlePayerIdSaved}
                          onError={setPayerIdError}
                        />
                      )}
                    </div>
                    {payerIdError && (
                      <div className="payer-id-error" role="alert">
                        {payerIdError}
                        <button type="button" onClick={() => setPayerIdError(null)} aria-label="Dismiss">&times;</button>
                      </div>
                    )}
                    <button className="payer-search-link" onClick={() => setShowPayerSearch(true)}>Search all payers</button>
                  </>
                )}
              </div>
            </div>

            <button className="submit-button" onClick={handleCheck} disabled={!canSubmit}>
              {loading ? (
                <><span className="button-spinner"></span>Checking...</>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  Check Eligibility
                </>
              )}
            </button>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="results-panel">
          {error && (
            <div className="error-banner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" />
              </svg>
              <span>{error}</span>
              <button className="error-dismiss" onClick={() => setError(null)}>&times;</button>
            </div>
          )}

          {loading && (
            <div className="loading-state"><div className="spinner"></div><span>Checking eligibility...</span></div>
          )}

          {activeView === 'results' && (results || historyResultData) && !loading && (
            <div className="results-view">
              <EligibilityResults
                data={results || historyResultData}
                onShowHistory={selectedPatient && eligHistory.length > 0 ? () => { setActiveView('history'); setHistoryResultData(null); } : null}
                formContext={{
                  payerId: effectivePayerId,
                  payerName: formFields.payerName,
                  dateOfService: dateOfService,
                  serviceType: serviceType,
                  provider: (selectedPractice.providers || []).find(p => String(p.id) === String(selectedProviderId)),
                }}
              />
            </div>
          )}

          {activeView === 'history' && !loading && (
            <>
              {selectedPatient && eligHistory.length > 0 && (
                <div className="history-section">
                  <h3>Prior Eligibility Checks</h3>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th className="th-status">Result</th>
                        <th>Name</th>
                        <th>Payer</th>
                        <th>Plan</th>
                        <th>DOS</th>
                        <th>Checked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligHistory.map(check => (
                        <tr
                          key={check.id}
                          className="history-row"
                          onClick={() => {
                            if (check.raw_response) {
                              try {
                                const parsed = JSON.parse(check.raw_response);
                                parsed._checkedAt = check.checked_at;
                                setHistoryResultData(parsed);
                                setResults(null);
                                setActiveView('results');
                              } catch { /* malformed response */ }
                            }
                          }}
                        >
                          <td className="td-status">
                            {check.status === 'Active' ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#00a67d"/>
                                <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#d63031"/>
                                <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                            )}
                          </td>
                          <td>{check.subscriber_first_name} {check.subscriber_last_name}</td>
                          <td>{check.payer_name || check.payer_id}</td>
                          <td>{check.plan_name || '-'}</td>
                          <td>{formatDos(check.service_date)}</td>
                          <td>{formatCheckedAt(check.checked_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedPatient && eligHistory.length === 0 && !results && (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  <h3>No Prior Checks</h3>
                  <p>No eligibility checks found for {formatPatientName(selectedPatient.display_name)}.</p>
                </div>
              )}

              {!selectedPatient && !results && (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  <h3>Ready to Check Eligibility</h3>
                  <p>Search for a patient or fill in subscriber info manually to verify insurance coverage.</p>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EligibilityPage;
